import React, { FC, useState } from 'react'

// Libs
import moment from 'moment'

// Components
import ViewsLoader from '../../../../loader/ViewsLoader'

// Helpers
import { handleImageError } from '../../../../../helpers/handleImageError'
import useToasterHelper from '../../../../../helpers/ToasterHelper'
import { uploadPicture } from '../../../../../helpers/uploadPicture'
import { dataURItoBlob } from '../../../../../commonHelpers/helpers'

// Types
import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'

// Constants
import { MESSAGES_CONST } from '../../../../../const/messages-const'
import { CONST } from '../../../../../const/const'

const SIGNATURE_PATH = CONST.DATA.STORAGE.USERS.SIGNATURE_URL.PREFIX

interface JudgesSignatureProps {
  judgeIndex: number
  isLocked: boolean
  showLockedModal: () => void
  row: IRegistrationByDayInterface
  setRow: (value: IRegistrationByDayInterface) => void
}

export const JudgesSignature: FC<JudgesSignatureProps> = ({
  judgeIndex,
  row,
  setRow,
  isLocked,
  showLockedModal,
}) => {
  const toastFunctions = useToasterHelper()

  const [loading, setLoading] = useState(false)

  const onSignScoreCard = async () => {
    setLoading(true)

    if (!row.score?.judges?.[judgeIndex].name) {
      toastFunctions.error({
        message: 'Judge name not found',
      })
      return
    }

    try {
      const name = row.score.judges[judgeIndex].name
      const fontSize = 120
      const fontFamily = 'SignatureFont, sans-serif'
      const fontString = `${fontSize}px ${fontFamily}`

      // Ensure the font is loaded (very important!)
      await document.fonts.load(fontString)
      await document.fonts.ready

      // 1. Create a temporary canvas to measure text size
      const measureCanvas = document.createElement('canvas')
      const measureCtx = measureCanvas.getContext('2d')!
      measureCtx.font = fontString
      const textWidth = measureCtx.measureText(name).width
      const textHeight = fontSize * 1.2

      // 2. Create final canvas with appropriate dimensions
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(textWidth + 50)
      canvas.height = Math.ceil(textHeight)
      const ctx = canvas.getContext('2d')!

      // Draw the text
      ctx.font = fontString

      ctx.fillStyle = '#000'
      ctx.textBaseline = 'middle'
      ctx.fillText(name, 20, canvas.height / 2)

      // 3. Export to PNG to firebase
      const dataURL = dataURItoBlob(canvas.toDataURL('image/png'))

      const image = await uploadPicture(
        dataURL,
        SIGNATURE_PATH,
        `${name}-${new Date().getTime()}.png`
      )

      if (image && row.score?.judges) {
        row.score.judges[judgeIndex].signatureUrl = image

        row.score.judges[judgeIndex].signatureDate = new Date().toString()
        setRow({ ...row })
      }

      toastFunctions.success({ message: MESSAGES_CONST.JUDGE_SIGNED })
    } catch (err) {
      console.error('Signature generation failed:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="border-t border-SeabiscuitLightThemeColorD3 mt-4 mb-4 pt-4">
      <h4 className="text-[20px] mb-4">Judges Signature</h4>
      <div className="flex items-center gap-2">
        <img
          className="w-[45px] h-[45px] object-cover rounded-md"
          src={row.score?.judges?.[judgeIndex].picture}
          onError={(e) => handleImageError(e, row.score?.judges?.[judgeIndex].name ?? '')}
          alt=""
        />
        <div>
          <p className="text-[12px] text-SeabiscuitDark200ThemeColor/50">Judge {judgeIndex + 1}</p>
          <div>
            {row.score?.judges?.[judgeIndex].name} •{' '}
            {row.score?.judges?.[judgeIndex].signatureDate ? (
              <>
                <span className="opacity-50">
                  Last signed{' '}
                  {moment(row.score?.judges?.[judgeIndex].signatureDate).format(
                    'h:mm a, DD MMM YY'
                  )}
                </span>{' '}
                •{' '}
                <button
                  type="button"
                  className="opacity-50 underline hover:opacity-100"
                  disabled={loading}
                  onClick={async () => {
                    if (isLocked) showLockedModal()
                    else await onSignScoreCard()
                  }}
                >
                  Re-sign
                </button>
              </>
            ) : (
              <span className="opacity-50">Not yet signed</span>
            )}
          </div>
        </div>
      </div>
      <div className="relative mt-5 bg-SeabiscuitGrayThemeColor flex-col flex justify-center items-center rounded-md w-full h-[118px] p-4 text-center">
        {loading && (
          <div className="absolute top-0 left-0 w-full h-full bg-white/50 rounded-md">
            <ViewsLoader size="xsm" />
          </div>
        )}
        {row.score?.judges?.[judgeIndex].signatureUrl ? (
          <img
            className="w-full h-full max-h-[100px] max-w-[320px] object-contain"
            src={row.score?.judges?.[judgeIndex].signatureUrl ?? ''}
            alt=""
          />
        ) : (
          <button
            type="button"
            className="flex items-center gap-2 underline hover:no-underline"
            onClick={async () => {
              if (isLocked) showLockedModal()
              else await onSignScoreCard()
            }}
            disabled={loading}
          >
            <img className="w-5" src="/assets/cp_icons/QuillPen-1.svg" alt="" />
            Sign score card
          </button>
        )}
      </div>
    </div>
  )
}
